// COUPON LEAVES — pure leaf maths (plan 2026-10-28, « Leaf maths »).
//
// The interior prints intro + N album pages + outro, then is padded up to a
// multiple of 4 (A3 duplex, 4 album pages per sheet) by DUPLICATING THE OUTRO
// (pdfGenerator.generateInteriorPdf, interiorPagePadding.ts). Print page 1 is a
// right-hand page, so leaf k = print pages (2k−1, 2k): the odd page is the
// recto (front), the even page its verso (back).
//
// A leaf is a COUPON LEAF only when BOTH of its pages are pad pages — a leaf
// that carries one real page (the outro or the last album page) on one side can
// never be cut out. With at most 3 pad pages there is at most one such leaf:
//
//   N  | N+2 | padded | pad pages | coupon leaf
//   33 | 35  | 36     | 36        | —  → separate coupon PDF
//   34 | 36  | 36     | —         | —  → separate coupon PDF
//   35 | 37  | 40     | 38,39,40  | 39–40 (38 stays an outro copy)
//   36 | 38  | 40     | 39,40     | 39–40
//
// PRINT-ONLY. Nothing here may touch pricing or order.page_count.

import { roundUpToMultipleOf4 } from './interiorPagePadding';

export interface CouponLeaf {
  /** Recto — an odd print page (right-hand). */
  front: number;
  /** Verso — front + 1. */
  back: number;
}

export interface CouponLeafPlan {
  /** Pages the interior prints before padding (intro + content + outro). */
  totalBeforePad: number;
  /** Interior page count after the ×4 padding. */
  padded: number;
  /** 1-based print pages added by the padding. */
  padPages: number[];
  /** Fully free leaves (both sides are pad pages) → printed as coupons. */
  couponLeaves: CouponLeaf[];
  /** Pad pages that stay outro copies (exactly as today). */
  outroCopyPages: number[];
}

/** Print pages before padding: intro + content + outro, each bookend only when
 *  it prints (a themed book has no intro — bookStructure.printsIntro). */
export function printPagesBeforePad(contentPages: number, hasIntro: boolean, hasOutro: boolean): number {
  return Math.max(0, Math.floor(contentPages)) + (hasIntro ? 1 : 0) + (hasOutro ? 1 : 0);
}

export function planCouponLeaves(totalBeforePad: number): CouponLeafPlan {
  const total = Number.isFinite(totalBeforePad) ? Math.max(0, Math.floor(totalBeforePad)) : 0;
  const padded = total === 0 ? 0 : roundUpToMultipleOf4(total);
  const padPages: number[] = [];
  for (let p = total + 1; p <= padded; p++) padPages.push(p);
  const pad = new Set(padPages);
  const couponLeaves: CouponLeaf[] = [];
  for (let front = 1; front < padded; front += 2) {
    if (pad.has(front) && pad.has(front + 1)) couponLeaves.push({ front, back: front + 1 });
  }
  const onCoupon = new Set(couponLeaves.flatMap((l) => [l.front, l.back]));
  return {
    totalBeforePad: total,
    padded,
    padPages,
    couponLeaves,
    outroCopyPages: padPages.filter((p) => !onCoupon.has(p)),
  };
}

/** Where an order's coupons print: inside the FIRST album (by album_index) that
 *  has a fully free leaf, on that leaf. Null → no album has one; the caller
 *  makes a separate 2-page coupon PDF instead. */
export function pickCouponAlbum(
  albums: ReadonlyArray<{ album_index: number; totalBeforePad: number }>,
): { album_index: number; pages: [number, number] } | null {
  const sorted = [...albums].sort((a, b) => a.album_index - b.album_index);
  for (const a of sorted) {
    const leaf = planCouponLeaves(a.totalBeforePad).couponLeaves[0];
    if (leaf) return { album_index: a.album_index, pages: [leaf.front, leaf.back] };
  }
  return null;
}

// ─── Keeping coupon pages out of every customer picture ──────────────────────

/** Marker written into a print interior's Keywords when it holds coupon pages:
 *  `storiz-coupon-pages=39-40`. Lives in the PDF itself, so any raster of a
 *  stored print interior can tell, whatever the database says or when. */
export const COUPON_PAGES_KEYWORD = 'storiz-coupon-pages';

export function couponPagesKeyword(pages: readonly number[]): string {
  return `${COUPON_PAGES_KEYWORD}=${pages.join('-')}`;
}

/** The coupon pages a PDF declares (from its Keywords), [] when none. */
export function couponPagesFromKeywords(keywords: string | null | undefined): number[] {
  const m = new RegExp(`${COUPON_PAGES_KEYWORD}=([0-9-]+)`).exec(String(keywords ?? ''));
  if (!m) return [];
  return m[1].split('-').map((s) => parseInt(s, 10)).filter((n) => Number.isFinite(n) && n > 0);
}

/**
 * Which PDF page a customer picture of print page `n` is rasterised from. A
 * coupon page shows the page just before the coupon leaf instead — the outro
 * (or its copy), i.e. exactly what that page showed before coupons existed — so
 * the 3D book keeps its page count and never shows a code.
 */
export function previewSourcePage(n: number, couponPages: readonly number[]): number {
  if (!couponPages.length || !couponPages.includes(n)) return n;
  return Math.max(1, Math.min(...couponPages) - 1);
}

// ─── The printer's words (PrinterLinkPage) ───────────────────────────────────

/** orders.coupon_print, as the worker stores it (print-result). */
export type CouponPrintPlace = { album_index?: number; pages?: number[]; separate_pdf_path?: string } | null | undefined;

/** « Coupons : pages 39–40 (à découper) » for the album that carries them. */
export function couponNoteForAlbum(cp: CouponPrintPlace, albumIndex: number): string | null {
  if (!cp || cp.separate_pdf_path || cp.album_index !== albumIndex) return null;
  const pages = (cp.pages ?? []).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!pages.length) return null;
  const span = pages.length === 1 ? `page ${pages[0]}` : `pages ${pages[0]}–${pages[pages.length - 1]}`;
  return `Coupons : ${span} (à découper)`;
}

/** True when the order's coupons are a separate PDF (coupons.pdf). */
export function couponsAreSeparate(cp: CouponPrintPlace): boolean {
  return !!cp?.separate_pdf_path;
}
